import { photoSource, photoSet } from './28-image-sources.mjs';
// Shared DOM builders own toast timing and dialog focus restoration.
// Translation and icons are injected; authored content stays literal.
/** Create shared DOM builders and a single toast lifecycle with injected copy and icons. */
export function createUi({ uiText, icon }) {
  const surfaces = new Map();
  function positionSurface(surface, trigger, point) {
    surfaces.get(surface)?.();
    const position = () => {
      const viewport = window.visualViewport;
      const nav = document.querySelector('#mobile-nav');
      const geometry = thumbSurfaceGeometry({
        width: window.innerWidth,
        height: window.innerHeight,
        viewport,
        navHeight: nav && !nav.hidden ? nav.getBoundingClientRect().height : 0,
      });
      surface.style.setProperty('--surface-height', `${geometry.height}px`);
      surface.style.setProperty('--surface-bottom', `${geometry.bottom}px`);
      surface.style.setProperty('--surface-nav-space', `${geometry.navSpace}px`);
      surface.style.setProperty('--surface-width', `${geometry.width}px`);
      surface.style.setProperty('--surface-left', `${geometry.left}px`);
      surface.style.bottom = point ? 'auto' : '';
      if (trigger) {
        if (point) {
          const bounds = surface.getBoundingClientRect();
          const anchor = trigger.getBoundingClientRect();
          const location = contextPointPosition({
            point: point.anchor ? { x: anchor.left + anchor.width / 2, y: anchor.bottom } : point,
            bounds,
            width: window.innerWidth,
            height: window.innerHeight,
            viewport,
            navSpace: geometry.navSpace,
          });
          surface.style.left = `${location.left}px`;
          surface.style.top = `${location.top}px`;
        } else if (geometry.mobile) {
          surface.style.left = `${geometry.left + Math.max(8, (geometry.width - surface.getBoundingClientRect().width) / 2)}px`;
          surface.style.top = 'auto';
        } else {
          const anchor = trigger.getBoundingClientRect();
          const bounds = surface.getBoundingClientRect();
          surface.style.left = `${Math.max(8, Math.min(anchor.left, window.innerWidth - bounds.width - 8))}px`;
          surface.style.top = `${Math.max(8, Math.min(anchor.bottom + 8, window.innerHeight - bounds.height - 8))}px`;
        }
      }
    };
    window.addEventListener('resize', position);
    window.visualViewport?.addEventListener('resize', position);
    window.visualViewport?.addEventListener('scroll', position);
    surfaces.set(surface, () => {
      window.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('scroll', position);
      surfaces.delete(surface);
    });
    position();
  }
  let toastTimer, dialogModal, dialogOpener, dialogFocusKey;

  const e = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const append = (node, ...children) => {
    node.append(...children.filter(Boolean));
    return node;
  };

  const link = (text, href, cls = '') => {
    const a = e('a', cls, text);
    a.href = href;
    if (/(?:^|\s)(?:text-button|secondary-button|primary-button)(?:\s|$)/.test(cls)) {
      const routeIcons = {
        feed: 'house',
        learning: 'compass',
        courses: 'compass',
        course: 'arrow-right',
        friends: 'users',
        discover: 'user-plus',
        explore: 'images',
        saved: 'bookmark',
        search: 'search',
        profile: 'user',
        post: 'arrow-right',
        starter: 'sparkles',
      };
      const destination = href.split('/')[1]?.split('?')[0];
      const name = /^←|^Back\b/.test(text)
        ? 'arrow-left'
        : Object.hasOwn(routeIcons, destination)
          ? routeIcons[destination]
          : undefined;
      if (name) {
        a.textContent = text.replace(/[→←]/g, '').trim();
        a.prepend(icon(name));
      }
    }
    return a;
  };

  const initials = (name) =>
    name
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('');

  const avatar = (user, large = false) => {
    if (user.avatarImage) {
      const image = e('img', `avatar${large ? ' large' : ''}`);
      image.src = photoSource(user.avatarImage, large ? 640 : 160);
      image.alt = user.name;
      image.width = large ? 96 : 40;
      image.height = large ? 96 : 40;
      image.loading = 'lazy';
      return image;
    }
    return e(
      'span',
      `avatar color-${[...user.id].reduce((sum, c) => sum + c.charCodeAt(0), 0) % 3}${large ? ' large' : ''}`,
      initials(user.name),
    );
  };

  const time = (timestamp) => {
    const mins = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
    return mins < 1
      ? 'Just now'
      : mins < 60
        ? `${mins}m ago`
        : mins < 1440
          ? `${Math.floor(mins / 60)}h ago`
          : `${Math.floor(mins / 1440)}d ago`;
  };

  const field = (tag, attributes = {}) => {
    const node = e(tag);
    Object.assign(node, attributes);
    return node;
  };

  function toast(message) {
    const node = document.querySelector('#status');
    node.textContent = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (node.textContent = ''), 5000);
  }

  /** Native dialogs restore their logical opener even after a server-driven rerender. */
  function dialog(title, content) {
    const modal = document.querySelector('#modal');
    if (dialogModal !== modal) {
      dialogModal = modal;
      const footer = e('div', 'dialog-thumb-footer');
      footer.append(iconButton('x', 'Close dialog', () => modal.close()));
      modal.append(footer);
      modal.addEventListener('close', () => {
        surfaces.get(modal)?.();
        const replacement =
          dialogFocusKey &&
          [...document.querySelectorAll('[data-focus-key]')].find(
            (node) => node.dataset.focusKey === dialogFocusKey,
          );
        const target = replacement || dialogOpener;
        if (target?.isConnected) target.focus({ preventScroll: true });
      });
    }
    document.querySelector('#modal-title').textContent = uiText(title);
    document.querySelector('#modal-body').replaceChildren(content);
    if (!modal.open) {
      dialogOpener = document.activeElement;
      dialogFocusKey = dialogOpener?.dataset.focusKey;
      modal.showModal();
    }
    positionSurface(modal);
  }

  /** Close a native palette during rerender without parsing a newer CSS selector.
   * Older Safari rejects :popover-open even when no palette exists. Closed native
   * popovers are safe to hide; fallback dialogs retain their own lifecycle. */
  function closeContextMenu() {
    const palette = document.querySelector('#context-palette');
    if (typeof palette?.hidePopover === 'function') palette.hidePopover();
  }

  function supportsNativePalette() {
    const probe = document.createElement('div');
    if (typeof probe.showPopover !== 'function' || typeof probe.hidePopover !== 'function')
      return false;
    try {
      probe.matches(':popover-open');
      return true;
    } catch (error) {
      // A partially supported selector is a capability failure, not a failed action.
      if (error.name === 'SyntaxError') return false;
      throw error;
    }
  }

  /** Native nonmodal palette supports tap, keyboard, Escape and outside dismissal. */
  function contextMenu(trigger, title, content, point) {
    if (!supportsNativePalette()) {
      dialog(title, content);
      return () => document.querySelector('#modal').close();
    }
    let palette = document.querySelector('#context-palette');
    if (!palette) {
      palette = e('section', 'context-palette');
      palette.id = 'context-palette';
      palette.setAttribute('popover', 'auto');
      palette.setAttribute('role', 'dialog');
      document.body.append(palette);
    }
    if (!trigger.palettePointerBound) {
      trigger.palettePointerBound = true;
      trigger.addEventListener('pointerdown', () => {
        trigger.paletteWasOpen =
          palette.matches(':popover-open') && palette.sourceTrigger === trigger;
      });
    }
    if (
      trigger.paletteWasOpen ||
      (palette.matches(':popover-open') && palette.sourceTrigger === trigger)
    ) {
      trigger.paletteWasOpen = false;
      closeContextMenu();
      return () => {};
    }
    if (palette.matches(':popover-open')) closeContextMenu();
    palette.sourceTrigger = trigger;
    const close = () => {
      if (palette.matches(':popover-open')) closeContextMenu();
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
    };
    palette.replaceChildren(e('h2', 'sr-only', uiText(title)), content);
    palette.setAttribute('aria-label', uiText(title));
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-controls', palette.id);
    palette.ontoggle = (event) => {
      trigger.setAttribute('aria-expanded', String(event.newState === 'open'));
      if (event.newState === 'closed') surfaces.get(palette)?.();
    };
    palette.onkeydown = (event) => {
      const choices = [...palette.querySelectorAll('.palette-choice')];
      const index = choices.indexOf(document.activeElement);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? choices.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + choices.length) % choices.length;
      choices[next]?.focus();
    };
    palette.showPopover({ source: trigger });
    positionSurface(palette, trigger, point);
    palette.querySelector('input,[aria-pressed="true"],button')?.focus({ preventScroll: true });
    return close;
  }

  function button(text, action, cls = 'secondary-button') {
    const clean = text.replace(/[♡◆◇↗↻→←✦]/g, '').trim();
    const node = e('button', cls, uiText(clean));
    node.type = 'button';
    const actionIcons = {
      Comment: 'message-circle',
      Save: 'bookmark',
      Saved: 'bookmark',
      Share: 'share-2',
      Options: 'ellipsis',
      'Refresh feed': 'refresh-cw',
      Automatic: 'sparkles',
      'Sign out': 'log-out',
      'Read full post': 'book-open',
      'Edit profile': 'pencil',
      'Reset adaptation': 'rotate-ccw',
      'Restore hidden posts': 'undo-2',
      'Clear reading history': 'trash-2',
    };
    const name = /Like$/.test(clean)
      ? 'heart'
      : Object.hasOwn(actionIcons, clean)
        ? actionIcons[clean]
        : undefined;
    if (name) node.prepend(icon(name));
    // Preserve focus for native dialogs and across async rerenders. The guard
    // prevents duplicate commands without disabling focus. Reaction controls opt in
    // to concurrent activations because their own queue retains the latest desired state.
    let pending = false;
    node.addEventListener('click', async () => {
      if (pending && !node.concurrentAction) return;
      pending = true;
      node.focus({ preventScroll: true });
      node.setAttribute('aria-busy', 'true');
      try {
        await action(node);
      } catch (error) {
        toast(error.message);
      } finally {
        pending = false;
        if (!node.concurrentAction) node.removeAttribute('aria-busy');
      }
    });
    return node;
  }

  /** Icon actions retain translated accessible names and native button behavior. */
  function iconButton(name, label, action, count) {
    const node = button('', action, 'icon-button');
    node.setAttribute('aria-label', uiText(label));
    node.title = uiText(label);
    node.append(icon(name));
    if (count !== undefined) node.append(e('span', 'action-count', String(count)));
    return node;
  }

  const sectionTop = (title, detail) =>
    append(
      e('div', 'section-top'),
      e('h2', '', title),
      detail ? e('span', 'subtle', detail) : null,
    );

  const heading = (title, description) =>
    append(
      e('div', 'page-heading'),
      e('div', 'eyebrow', 'YOUR PEOPLE, YOUR PACE'),
      e('h1', 'page-title', uiText(title)),
      e('p', 'page-intro', description),
    );

  const empty = (text) => e('div', 'empty-state', text);
  return {
    e,
    append,
    link,
    initials,
    avatar,
    time,
    field,
    toast,
    dialog,
    contextMenu,
    closeContextMenu,
    button,
    iconButton,
    sectionTop,
    heading,
    empty,
    photoSource,
    photoSet,
  };
}

/** Compute visible CSS-pixel space without mistaking pinch zoom for a keyboard.
 * Native points/dp inform target sizes; this web geometry uses CSS pixels. */
export function thumbSurfaceGeometry({ width, height, viewport, navHeight = 0 }) {
  const mobile = width <= 620;
  const visibleHeight = viewport?.height ?? height;
  const bottom = Math.max(0, height - visibleHeight - (viewport?.offsetTop ?? 0));
  const keyboard = (viewport?.scale ?? 1) === 1 && height - visibleHeight > 120;
  return {
    mobile,
    keyboard,
    bottom,
    height: visibleHeight,
    width: viewport?.width ?? width,
    left: viewport?.offsetLeft ?? 0,
    navSpace: mobile && !keyboard ? navHeight : 0,
  };
}

/** Place a contextual control beside its held CSS-pixel point, clamped to the
 * visible viewport and navigation. Keyboard callers supply their button anchor. */
export function contextPointPosition({ point, bounds, width, height, viewport, navSpace = 0 }) {
  const leftEdge = (viewport?.offsetLeft ?? 0) + 8;
  const topEdge = (viewport?.offsetTop ?? 0) + 8;
  const rightEdge = leftEdge + (viewport?.width ?? width) - 16;
  const bottomEdge = topEdge + (viewport?.height ?? height) - navSpace - 16;
  const above = point.y - bounds.height - 12;
  return {
    left: Math.max(leftEdge, Math.min(point.x - bounds.width / 2, rightEdge - bounds.width)),
    top: Math.max(
      topEdge,
      Math.min(above >= topEdge ? above : point.y + 12, bottomEdge - bounds.height),
    ),
  };
}
